"use client";

import { c as _c } from "react/compiler-runtime";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { PanelLeftIcon } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import type { VariantProps } from "class-variance-authority";
const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";
interface SidebarContextProps {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
}
const SidebarContext = React.createContext<SidebarContextProps | null>(null);
function useSidebar() {
  const context = React.use(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}
function SidebarProvider(t0) {
  const $ = _c(36);
  let children;
  let className;
  let openProp;
  let props;
  let setOpenProp;
  let style;
  let t1;
  if ($[0] !== t0) {
    ({
      defaultOpen: t1,
      open: openProp,
      onOpenChange: setOpenProp,
      className,
      style,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = openProp;
    $[4] = props;
    $[5] = setOpenProp;
    $[6] = style;
    $[7] = t1;
  } else {
    children = $[1];
    className = $[2];
    openProp = $[3];
    props = $[4];
    setOpenProp = $[5];
    style = $[6];
    t1 = $[7];
  }
  const defaultOpen = t1 === undefined ? true : t1;
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  let t2;
  if ($[8] !== open || $[9] !== setOpenProp) {
    t2 = value => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    };
    $[8] = open;
    $[9] = setOpenProp;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const setOpen = t2;
  let t3;
  if ($[11] !== isMobile || $[12] !== setOpen) {
    t3 = () => isMobile ? setOpenMobile(_temp) : setOpen(_temp2);
    $[11] = isMobile;
    $[12] = setOpen;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const toggleSidebar = t3;
  let t4;
  let t5;
  if ($[14] !== toggleSidebar) {
    t4 = () => {
      const handleKeyDown = event => {
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleSidebar();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t5 = [toggleSidebar];
    $[14] = toggleSidebar;
    $[15] = t4;
    $[16] = t5;
  } else {
    t4 = $[15];
    t5 = $[16];
  }
  React.useEffect(t4, t5);
  const state = open ? "expanded" : "collapsed";
  let t6;
  if ($[17] !== isMobile || $[18] !== open || $[19] !== openMobile || $[20] !== setOpen || $[21] !== state || $[22] !== toggleSidebar) {
    t6 = {
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar
    };
    $[17] = isMobile;
    $[18] = open;
    $[19] = openMobile;
    $[20] = setOpen;
    $[21] = state;
    $[22] = toggleSidebar;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const contextValue = t6;
  let t7;
  if ($[24] !== style) {
    t7 = {
      "--sidebar-width": SIDEBAR_WIDTH,
      "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
      ...style
    };
    $[24] = style;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const t8 = t7 as React.CSSProperties;
  let t9;
  if ($[26] !== className) {
    t9 = cn("group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full", className);
    $[26] = className;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== children || $[29] !== props || $[30] !== t8 || $[31] !== t9) {
    t10 = <TooltipProvider delayDuration={0}><div data-slot="sidebar-wrapper" style={t8} className={t9} {...props}>{children}</div></TooltipProvider>;
    $[28] = children;
    $[29] = props;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== contextValue || $[34] !== t10) {
    t11 = <SidebarContext value={contextValue}>{t10}</SidebarContext>;
    $[33] = contextValue;
    $[34] = t10;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}
function _temp2(open_1) {
  return !open_1;
}
function _temp(open_0) {
  return !open_0;
}
function Sidebar(t0) {
  const $ = _c(57);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      side: t1,
      variant: t2,
      collapsible: t3,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  const side = t1 === undefined ? "left" : t1;
  const variant = t2 === undefined ? "sidebar" : t2;
  const collapsible = t3 === undefined ? "offcanvas" : t3;
  const {
    isMobile,
    state,
    openMobile,
    setOpenMobile
  } = useSidebar();
  const {
    t
  } = useI18n();
  if (collapsible === "none") {
    let t4;
    if ($[7] !== className) {
      t4 = cn("bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col", className);
      $[7] = className;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== children || $[10] !== props || $[11] !== t4) {
      t5 = <div data-slot="sidebar" className={t4} {...props}>{children}</div>;
      $[9] = children;
      $[10] = props;
      $[11] = t4;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    return t5;
  }
  if (isMobile) {
    let t4;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        "--sidebar-width": SIDEBAR_WIDTH_MOBILE
      };
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    if ($[14] !== t) {
      t5 = t("admin.ui.sidebar");
      $[14] = t;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== t5) {
      t6 = <SheetTitle>{t5}</SheetTitle>;
      $[16] = t5;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== t) {
      t7 = t("admin.ui.sidebarDescription");
      $[18] = t;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== t7) {
      t8 = <SheetDescription>{t7}</SheetDescription>;
      $[20] = t7;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== t6 || $[23] !== t8) {
      t9 = <SheetHeader className="sr-only">{t6}{t8}</SheetHeader>;
      $[22] = t6;
      $[23] = t8;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== children) {
      t10 = <div className="flex h-full w-full flex-col">{children}</div>;
      $[25] = children;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    let t11;
    if ($[27] !== side || $[28] !== t10 || $[29] !== t9) {
      t11 = <SheetContent data-sidebar="sidebar" data-slot="sidebar" data-mobile="true" className="bg-sidebar text-sidebar-foreground w-(--sidebar-width) p-0 [&>button]:hidden" style={t4 as React.CSSProperties} side={side}>{t9}{t10}</SheetContent>;
      $[27] = side;
      $[28] = t10;
      $[29] = t9;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    let t12;
    if ($[31] !== openMobile || $[32] !== props || $[33] !== setOpenMobile || $[34] !== t11) {
      t12 = <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>{t11}</Sheet>;
      $[31] = openMobile;
      $[32] = props;
      $[33] = setOpenMobile;
      $[34] = t11;
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    return t12;
  }
  const t4 = state === "collapsed" ? collapsible : "";
  const t5 = variant === "floating" || variant === "inset" ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]" : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)";
  let t6;
  if ($[36] !== t5) {
    t6 = cn("relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear", "group-data-[collapsible=offcanvas]:w-0", "group-data-[side=right]:rotate-180", t5);
    $[36] = t5;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  let t7;
  if ($[38] !== t6) {
    t7 = <div data-slot="sidebar-gap" className={t6} />;
    $[38] = t6;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  const t8 = side === "left" ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]" : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]";
  const t9 = variant === "floating" || variant === "inset" ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]" : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l";
  let t10;
  if ($[40] !== className || $[41] !== t8 || $[42] !== t9) {
    t10 = cn("fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear md:flex", t8, t9, className);
    $[40] = className;
    $[41] = t8;
    $[42] = t9;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== children) {
    t11 = <div data-sidebar="sidebar" data-slot="sidebar-inner" className="bg-sidebar group-data-[variant=floating]:border-sidebar-border flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm">{children}</div>;
    $[44] = children;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== props || $[47] !== t10 || $[48] !== t11) {
    t12 = <div data-slot="sidebar-container" className={t10} {...props}>{t11}</div>;
    $[46] = props;
    $[47] = t10;
    $[48] = t11;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] !== side || $[51] !== state || $[52] !== t12 || $[53] !== t4 || $[54] !== t7 || $[55] !== variant) {
    t13 = <div className="group peer text-sidebar-foreground hidden md:block" data-state={state} data-collapsible={t4} data-variant={variant} data-side={side} data-slot="sidebar">{t7}{t12}</div>;
    $[50] = side;
    $[51] = state;
    $[52] = t12;
    $[53] = t4;
    $[54] = t7;
    $[55] = variant;
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  return t13;
}
function SidebarTrigger(t0) {
  const $ = _c(19);
  let className;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onClick;
    $[3] = props;
  } else {
    className = $[1];
    onClick = $[2];
    props = $[3];
  }
  const {
    toggleSidebar
  } = useSidebar();
  const {
    t
  } = useI18n();
  let t1;
  if ($[4] !== className) {
    t1 = cn("size-7", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== onClick || $[7] !== toggleSidebar) {
    t2 = event => {
      onClick?.(event);
      toggleSidebar();
    };
    $[6] = onClick;
    $[7] = toggleSidebar;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <PanelLeftIcon />;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t) {
    t4 = t("admin.ui.toggleSidebar");
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <span className="sr-only">{t4}</span>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== props || $[15] !== t1 || $[16] !== t2 || $[17] !== t5) {
    t6 = <Button data-sidebar="trigger" data-slot="sidebar-trigger" variant="ghost" size="icon" className={t1} onClick={t2} {...props}>{t3}{t5}</Button>;
    $[14] = props;
    $[15] = t1;
    $[16] = t2;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
function SidebarRail(t0) {
  const $ = _c(15);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    toggleSidebar
  } = useSidebar();
  const {
    t
  } = useI18n();
  let t1;
  if ($[3] !== t) {
    t1 = t("admin.ui.toggleSidebar");
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t) {
    t2 = t("admin.ui.toggleSidebar");
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== className) {
    t3 = cn("hover:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] sm:flex", "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize", "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize", "hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full", "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2", "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props || $[10] !== t1 || $[11] !== t2 || $[12] !== t3 || $[13] !== toggleSidebar) {
    t4 = <button data-sidebar="rail" data-slot="sidebar-rail" aria-label={t1} tabIndex={-1} onClick={toggleSidebar} title={t2} className={t3} {...props} />;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = toggleSidebar;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
function SidebarInset(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-background relative flex min-w-0 w-full flex-1 flex-col", "md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <main data-slot="sidebar-inset" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarInput(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-background h-8 w-full shadow-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Input data-slot="sidebar-input" data-sidebar="input" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarHeader(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-2 p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-header" data-sidebar="header" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarFooter(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-2 p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-footer" data-sidebar="footer" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-sidebar-border mx-2 w-auto", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Separator data-slot="sidebar-separator" data-sidebar="separator" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-content" data-sidebar="content" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("relative flex w-full min-w-0 flex-col p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-group" data-sidebar="group" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarGroupLabel(t0) {
  const $ = _c(10);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot : "div";
  let t2;
  if ($[4] !== className) {
    t2 = cn("text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0", "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== Comp || $[7] !== props || $[8] !== t2) {
    t3 = <Comp data-slot="sidebar-group-label" data-sidebar="group-label" className={t2} {...props} />;
    $[6] = Comp;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function SidebarGroupAction(t0) {
  const $ = _c(10);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot : "button";
  let t2;
  if ($[4] !== className) {
    t2 = cn("text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0", "after:absolute after:-inset-2 md:after:hidden", "group-data-[collapsible=icon]:hidden", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== Comp || $[7] !== props || $[8] !== t2) {
    t3 = <Comp data-slot="sidebar-group-action" data-sidebar="group-action" className={t2} {...props} />;
    $[6] = Comp;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function SidebarGroupContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("w-full text-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-group-content" data-sidebar="group-content" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenu(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex w-full min-w-0 flex-col gap-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ul data-slot="sidebar-menu" data-sidebar="menu" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group/menu-item relative", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <li data-slot="sidebar-menu-item" data-sidebar="menu-item" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
const sidebarMenuButtonVariants = cva("peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0", {
  variants: {
    variant: {
      default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
      outline: "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]"
    },
    size: {
      default: "h-8 text-sm",
      sm: "h-7 text-xs",
      lg: "h-12 text-sm group-data-[collapsible=icon]:p-0!"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
function SidebarMenuButton(t0) {
  const $ = _c(28);
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let tooltip;
  if ($[0] !== t0) {
    ({
      asChild: t1,
      isActive: t2,
      variant: t3,
      size: t4,
      tooltip,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = tooltip;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    tooltip = $[7];
  }
  const asChild = t1 === undefined ? false : t1;
  const isActive = t2 === undefined ? false : t2;
  const variant = t3 === undefined ? "default" : t3;
  const size = t4 === undefined ? "default" : t4;
  const Comp = asChild ? Slot : "button";
  const {
    isMobile,
    state
  } = useSidebar();
  let t5;
  if ($[8] !== className || $[9] !== size || $[10] !== variant) {
    t5 = cn(sidebarMenuButtonVariants({
      variant,
      size
    }), className);
    $[8] = className;
    $[9] = size;
    $[10] = variant;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== Comp || $[13] !== isActive || $[14] !== props || $[15] !== size || $[16] !== t5) {
    t6 = <Comp data-slot="sidebar-menu-button" data-sidebar="menu-button" data-size={size} data-active={isActive} className={t5} {...props} />;
    $[12] = Comp;
    $[13] = isActive;
    $[14] = props;
    $[15] = size;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const button = t6;
  if (!tooltip) {
    return button;
  }
  if (typeof tooltip === "string") {
    let t7;
    if ($[18] !== tooltip) {
      t7 = {
        children: tooltip
      };
      $[18] = tooltip;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    tooltip = t7;
  }
  let t7;
  if ($[20] !== button) {
    t7 = <TooltipTrigger asChild={true}>{button}</TooltipTrigger>;
    $[20] = button;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const t8 = state !== "collapsed" || isMobile;
  let t9;
  if ($[22] !== t8 || $[23] !== tooltip) {
    t9 = <TooltipContent side="right" align="center" hidden={t8} {...tooltip} />;
    $[22] = t8;
    $[23] = tooltip;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t7 || $[26] !== t9) {
    t10 = <Tooltip>{t7}{t9}</Tooltip>;
    $[25] = t7;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
function SidebarMenuAction(t0) {
  const $ = _c(12);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      asChild: t1,
      showOnHover: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const asChild = t1 === undefined ? false : t1;
  const showOnHover = t2 === undefined ? false : t2;
  const Comp = asChild ? Slot : "button";
  const t3 = showOnHover && "peer-data-[active=true]/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0";
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cn("text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0", "after:absolute after:-inset-2 md:after:hidden", "peer-data-[size=sm]/menu-button:top-1", "peer-data-[size=default]/menu-button:top-1.5", "peer-data-[size=lg]/menu-button:top-2.5", "group-data-[collapsible=icon]:hidden", t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== Comp || $[9] !== props || $[10] !== t4) {
    t5 = <Comp data-slot="sidebar-menu-action" data-sidebar="menu-action" className={t4} {...props} />;
    $[8] = Comp;
    $[9] = props;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function SidebarMenuBadge(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-sidebar-foreground pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums select-none", "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground", "peer-data-[size=sm]/menu-button:top-1", "peer-data-[size=default]/menu-button:top-1.5", "peer-data-[size=lg]/menu-button:top-2.5", "group-data-[collapsible=icon]:hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-menu-badge" data-sidebar="menu-badge" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuSkeleton(t0) {
  const $ = _c(14);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      showIcon: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const showIcon = t1 === undefined ? false : t1;
  const width = `${Math.floor(Math.random() * 40) + 50}%`;
  let t2;
  if ($[4] !== className) {
    t2 = cn("flex h-8 items-center gap-2 rounded-md px-2", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== showIcon) {
    t3 = showIcon && <Skeleton className="size-4 rounded-md" data-sidebar="menu-skeleton-icon" />;
    $[6] = showIcon;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      "--skeleton-width": width
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Skeleton className="h-4 max-w-(--skeleton-width) flex-1" data-sidebar="menu-skeleton-text" style={t4 as React.CSSProperties} />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== props || $[11] !== t2 || $[12] !== t3) {
    t6 = <div data-slot="sidebar-menu-skeleton" data-sidebar="menu-skeleton" className={t2} {...props}>{t3}{t5}</div>;
    $[10] = props;
    $[11] = t2;
    $[12] = t3;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function SidebarMenuSub(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("border-sidebar-border mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l px-2.5 py-0.5", "group-data-[collapsible=icon]:hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ul data-slot="sidebar-menu-sub" data-sidebar="menu-sub" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuSubItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group/menu-sub-item relative", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <li data-slot="sidebar-menu-sub-item" data-sidebar="menu-sub-item" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuSubButton(t0) {
  const $ = _c(16);
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      asChild: t1,
      size: t2,
      isActive: t3,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  const asChild = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "md" : t2;
  const isActive = t3 === undefined ? false : t3;
  const Comp = asChild ? Slot : "a";
  const t4 = size === "sm" && "text-xs";
  const t5 = size === "md" && "text-sm";
  let t6;
  if ($[6] !== className || $[7] !== t4 || $[8] !== t5) {
    t6 = cn("text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 outline-hidden focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0", "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground", t4, t5, "group-data-[collapsible=icon]:hidden", className);
    $[6] = className;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== Comp || $[11] !== isActive || $[12] !== props || $[13] !== size || $[14] !== t6) {
    t7 = <Comp data-slot="sidebar-menu-sub-button" data-sidebar="menu-sub-button" data-size={size} data-active={isActive} className={t6} {...props} />;
    $[10] = Comp;
    $[11] = isActive;
    $[12] = props;
    $[13] = size;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
export { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupAction, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInput, SidebarInset, SidebarMenu, SidebarMenuAction, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, SidebarProvider, SidebarRail, SidebarSeparator, SidebarTrigger, useSidebar };